import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
} from '../SettingsShell/SettingsPage';
import { SetChip, SetSwitch } from '../SettingsShell/controls';
import { OperatoryEditModal } from './OperatoryEditModal';
import { ROLE_LABEL } from '../../settings-practice/types';
import type { Operatory } from '../../settings-practice/types';

export interface OperatoriesViewProps {
  operatories: Operatory[];
  location: string;
  lastSync: string;
  /** Persist a changed room (in-service switch or the edit modal). */
  onChange?: (next: Operatory) => void | Promise<void>;
}

/** Practice management › Operatories (spec 15 §6). The list stays a list. */
export function OperatoriesView({ operatories, location, lastSync, onChange }: OperatoriesViewProps) {
  const [rooms, setRooms] = useState(operatories);
  const [editing, setEditing] = useState<Operatory | null>(null);

  // Derived from the rows (§3.1).
  const inService = rooms.filter((r) => !r.outOfService).length;

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Practice management"
        titleLead="Operatories"
        sub={
          <>Your rooms: which are usable and what each is for.</>
        }
      />

      <SettingsCards>
        <SettingsCard
          title="Rooms"
          sub={
            <>
              <strong className="text-[var(--text-secondary)]">Synced from the practice management system.</strong>{' '}
              Rooms are created and removed there; what they are for, and whether they are usable today, is set here.
            </>
          }
          headRight={
            <span className="text-[12px] text-[var(--text-muted)]">
              <strong className="text-[var(--text-secondary)]">{inService}</strong> in service · {rooms.length} rooms
            </span>
          }
        >
          {rooms.map((r, i) => (
            <div
              key={r.id}
              className="flex items-center gap-4 px-6 py-[18px]"
              style={i === rooms.length - 1 ? undefined : { borderBottom: '1px solid #edf0f6' }}
            >
              <button
                type="button"
                onClick={() => { setEditing(r); }}
                className="flex min-w-0 flex-1 items-center gap-4 text-left"
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
                  style={{ background: r.outOfService ? '#F1F3F9' : 'var(--brand-indigo-tint)' }}
                >
                  <Icon
                    kind="chair"
                    size={16}
                    stroke={1.9}
                    style={{ color: r.outOfService ? 'var(--text-muted)' : 'var(--brand-indigo)' }}
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-[13.5px] font-semibold text-[var(--text-primary)]">{r.name}</span>
                    {r.isDefault ? <SetChip>Default</SetChip> : null}
                  </span>
                  {r.note ? (
                    <span className="mt-0.5 block text-[11.5px] text-[var(--warn-amber)]">{r.note}</span>
                  ) : null}
                </span>
                <span className="flex min-w-0 flex-wrap justify-end gap-1.5">
                  {r.acceptsRoles.map((role) => (
                    <SetChip key={role} tone="grey">
                      {ROLE_LABEL[role]}
                    </SetChip>
                  ))}
                </span>
              </button>
              <span className="flex shrink-0 items-center gap-3">
                <span className="w-[86px] text-right text-[12px] text-[var(--text-muted)]">
                  {r.outOfService ? 'Out of service' : 'In service'}
                </span>
                <SetSwitch
                  label={`${r.name} in service`}
                  checked={!r.outOfService}
                  onChange={(next) => {
                    setRooms((prev) =>
                      prev.map((x) => (x.id === r.id ? { ...x, outOfService: !next } : x)),
                    );
                    void onChange?.({ ...r, outOfService: !next });
                  }}
                />
              </span>
            </div>
          ))}

          <SettingsNote>Mark a room Out of service and ELVA stops counting it.</SettingsNote>
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter
        location={location}
        left={
          <>
            Last sync <strong className="text-[var(--text-secondary)]">{lastSync}</strong>. {rooms.length} rooms, no
            changes.
          </>
        }
      />

      <OperatoryEditModal
        operatory={editing}
        roomCount={rooms.length}
        roomIndex={editing ? rooms.findIndex((r) => r.id === editing.id) + 1 : 0}
        onClose={() => { setEditing(null); }}
        onSave={(next) => {
          setRooms((prev) =>
            prev.map((x) =>
              x.id === next.id
                ? next
                : // One room carries the default — turning it on here turns it off wherever it is now.
                  next.isDefault
                  ? { ...x, isDefault: false }
                  : x,
            ),
          );
          setEditing(null);
          void onChange?.(next);
        }}
      />
    </div>
  );
}
